import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { toast } from "sonner";
import { Play, Trash } from "@phosphor-icons/react";
import { useI18n } from "@/lib/i18n";

export default function TestCenter() {
  const { t } = useI18n();
  const [products, setProducts] = useState([]);
  const [pallets, setPallets] = useState([]);
  const [runs, setRuns] = useState([]);
  const [pid, setPid] = useState("");
  const [qty, setQty] = useState({});
  const [expected, setExpected] = useState(1);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedRuns, setSelectedRuns] = useState(new Set());
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    const [p, pt, r] = await Promise.all([
      api.get("/products"), api.get("/pallet-types"), api.get("/test-center/runs"),
    ]);
    setProducts(p.data); setPallets(pt.data); setRuns(r.data);
    if (pt.data[0]) setPid(pt.data[0].id);
  };
  useEffect(() => { load(); }, []);

  const run = async () => {
    const items = Object.entries(qty).filter(([, v]) => v > 0).map(([sku, quantity]) => ({ sku, quantity: Number(quantity) }));
    if (!items.length) return toast.error(t("final.test_need_product"));
    setLoading(true);
    try {
      const r = await api.post("/test-center/compare", { items, pallet_type_id: pid, expected_pallets: Number(expected) });
      setResult(r.data);
      const runsR = await api.get("/test-center/runs");
      setRuns(runsR.data);
      toast.success(`Karşılaştırma: %${r.data.comparison.accuracy} doğruluk`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const toggleSel = (id) => {
    setSelectedRuns((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    if (selectedRuns.size === runs.length) setSelectedRuns(new Set());
    else setSelectedRuns(new Set(runs.map((r) => r.id)));
  };
  const bulkDelete = async () => {
    if (selectedRuns.size === 0) return;
    if (!window.confirm(`${selectedRuns.size} test kaydı silinsin mi?`)) return;
    setDeleting(true);
    try {
      const r = await api.post("/test-center/runs/bulk-delete", { ids: Array.from(selectedRuns) });
      toast.success(`${r.data.deleted} test kaydı silindi`);
      setSelectedRuns(new Set());
      const runsR = await api.get("/test-center/runs");
      setRuns(runsR.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setDeleting(false); }
  };

  return (
    <div>
      <PageHeader title={t("test.title")} subtitle={t("test.subtitle")} />
      <div className="p-8 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-1 space-y-3">
          <div className="bg-white border border-slate-200 p-5 space-y-3">
            <h3 className="font-display font-bold text-sm uppercase tracking-wider text-slate-700">Test Girdileri</h3>
            <label className="block text-[10px] uppercase font-semibold tracking-wider text-slate-500">{t("final.test_pallet_type")}
              <select className="input mt-1" value={pid} onChange={(e) => setPid(e.target.value)}>
                {pallets.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </label>
            <label className="block text-[10px] uppercase font-semibold tracking-wider text-slate-500">{t("final.test_expected")}
              <input type="number" min={1} className="input mt-1" value={expected} onChange={(e) => setExpected(e.target.value)} />
            </label>
            <div className="max-h-72 overflow-y-auto space-y-1 mt-2">
              {products.map((p) => (
                <div key={p.id} className="flex items-center gap-2">
                  <div className="flex-1 text-sm"><b>{p.inch}"</b> <span className="font-mono text-[10px] text-slate-500">{p.sku}</span></div>
                  <input type="number" min={0} data-testid={`test-qty-${p.sku}`}
                    className="w-20 border border-slate-300 px-2 py-1 text-sm font-mono outline-none focus:border-[#2563EB]"
                    value={qty[p.sku] || 0}
                    onChange={(e) => setQty({ ...qty, [p.sku]: Number(e.target.value) })} />
                </div>
              ))}
            </div>
            <button onClick={run} disabled={loading} data-testid="run-test"
              className="w-full mt-2 bg-[#2563EB] hover:bg-[#1D4ED8] disabled:opacity-50 text-white py-2.5 font-semibold rounded-sm inline-flex items-center justify-center gap-2">
              <Play size={16} weight="fill" /> {loading ? "Karşılaştırılıyor…" : t("test.compare")}
            </button>
          </div>
        </div>
        <div className="lg:col-span-2 space-y-4">
          {result && (
            <div className={`bg-white border-l-4 ${result.comparison.diff === 0 ? "border-green-500" : "border-yellow-500"} border-y border-r border-slate-200 p-5`}>
              <div className="grid grid-cols-4 gap-4">
                <M l="Beklenen" v={result.comparison.expected_pallets} />
                <M l="Algoritma" v={result.comparison.algo_pallets} />
                <M l="Fark" v={result.comparison.diff} c={result.comparison.diff === 0 ? "#16A34A" : "#DC2626"} />
                <M l={t("test.accuracy")} v={`%${result.comparison.accuracy}`} c="#2563EB" />
              </div>
            </div>
          )}
          <div className="bg-white border border-slate-200">
            <div className="border-b border-slate-200 px-5 py-3 flex items-center justify-between gap-2">
              <h3 className="font-display font-bold text-sm uppercase tracking-wider text-slate-700">{t("test.history")}</h3>
              <div className="flex items-center gap-2">
                {selectedRuns.size > 0 && (
                  <span className="text-xs font-semibold text-slate-600" data-testid="selected-runs-count">
                    {selectedRuns.size} {t("final.selected")}
                  </span>
                )}
                <button
                  data-testid="btn-bulk-delete-runs"
                  onClick={bulkDelete}
                  disabled={selectedRuns.size === 0 || deleting}
                  className="inline-flex items-center gap-1 text-xs bg-red-600 hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed text-white px-3 py-1.5 font-semibold rounded-sm">
                  <Trash size={12} weight="bold" /> {deleting ? t("test.deleting") : t("test.bulk_delete")}
                </button>
              </div>
            </div>
            <div className="overflow-x-auto">
            <table className="data-table w-full min-w-[720px]">
              <thead><tr>
                <th className="w-10 text-center">
                  <input type="checkbox" data-testid="select-all-runs"
                    checked={runs.length > 0 && selectedRuns.size === runs.length}
                    onChange={toggleAll}
                    className="w-4 h-4 cursor-pointer accent-[#2563EB]" />
                </th>
                <th>{t("test.date")}</th><th className="text-right">{t("test.expected")}</th><th className="text-right">{t("test.algorithm")}</th>
                <th className="text-right">{t("test.diff")}</th><th className="text-right">{t("test.accuracy")}</th><th className="text-right">{t("test.fill")}</th>
              </tr></thead>
              <tbody>
                {runs.map((r) => (
                  <tr key={r.id} className={selectedRuns.has(r.id) ? "bg-blue-50" : ""}>
                    <td className="text-center">
                      <input type="checkbox" data-testid={`run-checkbox-${r.id}`}
                        checked={selectedRuns.has(r.id)}
                        onChange={() => toggleSel(r.id)}
                        className="w-4 h-4 cursor-pointer accent-[#2563EB]" />
                    </td>
                    <td className="font-mono text-xs">{r.created_at?.slice(0, 16)}</td>
                    <td className="text-right data-cell">{r.expected_pallets}</td>
                    <td className="text-right data-cell">{r.algo_pallets}</td>
                    <td className={`text-right data-cell ${r.diff === 0 ? "text-green-600" : "text-red-600"}`}>{r.diff}</td>
                    <td className="text-right data-cell font-bold">%{r.accuracy}</td>
                    <td className="text-right data-cell">{(r.fill_ratio * 100).toFixed(1)}%</td>
                  </tr>
                ))}
                {runs.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-slate-400">{t("test.none")}</td></tr>}
              </tbody>
            </table>
            </div>
          </div>
        </div>
      </div>
      <style>{`.input{width:100%;border:1px solid #cbd5e1;padding:0.4rem 0.55rem;font-size:0.85rem;font-family:'JetBrains Mono',monospace;outline:none}.input:focus{border-color:#2563EB;box-shadow:0 0 0 3px rgba(37,99,235,0.15)}`}</style>
    </div>
  );
}

const M = ({ l, v, c }) => (
  <div><div className="text-[10px] uppercase text-slate-500">{l}</div>
    <div className="font-display text-2xl font-bold" style={{ color: c || "#0F172A" }}>{v}</div></div>
);
